import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // Increase timeout to 2 minutes for sync operations
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      // Only reload if we're not already on login page and not during token validation
      if (window.location.pathname !== '/login' && !error.config?.url?.endsWith('/auth/me')) {
        window.location.href = '/login';
      }
    }
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

export const uploadImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

export const uploadImages = async (files) => {
  const formData = new FormData();
  files.forEach(file => {
    formData.append('files', file);
  });

  const response = await api.post('/upload/batch', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

export const searchImages = async (query, page = 1, perPage = 100) => {
  const response = await api.get('/search', {
    params: {
      query,
      page,
      per_page: perPage
    }
  });

  return response.data;
};

export const searchByImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post('/search/image', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

export const getAllImages = async (page = 1, perPage = 20, sortBy = 'date', filters = {}) => {
  const params = {
    page,
    per_page: perPage,
    sort_by: sortBy
  };

  // Add file type filter if selected
  if (filters.fileType && filters.fileType.length > 0) {
    params.file_type = filters.fileType[0]; // API expects single value
  }

  // Add stock source filter if selected
  if (filters.stockSource && filters.stockSource.length > 0) {
    params.stock_source = filters.stockSource[0]; // API expects single value
  }

  // Add people count filter if selected
  if (filters.peopleCount && filters.peopleCount.length > 0) {
    params.people_count = filters.peopleCount[0]; // API expects single value
  }

  const response = await api.get('/images', { params });

  return response.data;
};

export const getImageDetails = async (imageId) => {
  const response = await api.get(`/images/${imageId}`);
  return response.data;
};

export const deleteImage = async (imageId) => {
  const response = await api.delete(`/images/${imageId}`);
  return response.data;
};

export const analyzeImage = async (imageId) => {
  const response = await api.post(`/analyze/${imageId}`);
  return response.data;
};

export const getImageUrl = (imagePath, s3Url, imageId) => {
  // Prefer presigned S3 URL for direct access (fastest)
  if (s3Url) return s3Url;
  // Fallback to backend serving endpoint
  if (imageId) return `${API_BASE_URL}/serve/image/${imageId}`;
  if (!imagePath) return '';
  return `${API_BASE_URL}/static/${imagePath.replace('./uploads/', '')}`;
};

export const getThumbnailUrl = (thumbnailPath, thumbnailS3Url, imageId) => {
  // Prefer presigned S3 URL for direct access (fastest)
  if (thumbnailS3Url) return thumbnailS3Url;
  // Fallback to backend serving endpoint
  if (imageId) return `${API_BASE_URL}/serve/thumbnail/${imageId}`;
  if (!thumbnailPath) return '';
  return `${API_BASE_URL}/static/thumbnails/${thumbnailPath.replace('./thumbnails/', '')}`;
};

export const getDownloadUrl = (imageId) => {
  // Always use backend serving endpoint for downloads to avoid CORS issues
  return `${API_BASE_URL}/serve/image/${imageId}?download=true`;
};

// Album Management Functions
export const getAllAlbums = async () => {
  const response = await api.get('/albums');
  return response.data;
};

export const createAlbum = async (name, description) => {
  const response = await api.post('/albums', { name, description });
  return response.data;
};

export const getAlbumDetails = async (albumId) => {
  const response = await api.get(`/albums/${albumId}`);
  return response.data;
};

export const getAlbumImages = async (albumId, page = 1, perPage = 20) => {
  const response = await api.get(`/albums/${albumId}/images`, {
    params: { page, per_page: perPage }
  });
  return response.data;
};

export const updateAlbum = async (albumId, updates) => {
  const response = await api.put(`/albums/${albumId}`, updates);
  return response.data;
};

export const deleteAlbum = async (albumId) => {
  const response = await api.delete(`/albums/${albumId}`);
  return response.data;
};

export const addImageToAlbum = async (albumId, imageId) => {
  const response = await api.post(`/albums/${albumId}/images`, { image_id: imageId });
  return response.data;
};

export const removeImageFromAlbum = async (albumId, imageId) => {
  const response = await api.delete(`/albums/${albumId}/images/${imageId}`);
  return response.data;
};

// Label Management API
export const getImageLabels = async (imageId) => {
  const response = await api.get(`/images/${imageId}/labels`);
  return response.data;
};

export const addImageLabel = async (imageId, labelData) => {
  const response = await api.post(`/images/${imageId}/labels`, labelData);
  return response.data;
};

export const updateImageLabel = async (imageId, labelId, labelData) => {
  const response = await api.put(`/images/${imageId}/labels/${labelId}`, labelData);
  return response.data;
};

export const deleteImageLabel = async (imageId, labelId) => {
  const response = await api.delete(`/images/${imageId}/labels/${labelId}`);
  return response.data;
};

// Object Management API
export const getImageObjects = async (imageId) => {
  const response = await api.get(`/images/${imageId}/objects`);
  return response.data;
};

export const addImageObject = async (imageId, objectData) => {
  const response = await api.post(`/images/${imageId}/objects`, objectData);
  return response.data;
};

export const updateImageObject = async (imageId, objectId, objectData) => {
  const response = await api.put(`/images/${imageId}/objects/${objectId}`, objectData);
  return response.data;
};

export const deleteImageObject = async (imageId, objectId) => {
  const response = await api.delete(`/images/${imageId}/objects/${objectId}`);
  return response.data;
};

// Auth validation
export const validateToken = async () => {
  try {
    const response = await api.get('/auth/me');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export { API_BASE_URL };
export default api;